import { useCallback, useEffect, useRef, useState } from 'react'
import { MdOutlineSearch } from 'react-icons/md'
import { useInView } from 'react-intersection-observer'

import type { ChangeEvent, ReactElement } from 'react'
import type { ICatalogTemplate } from '~/types/catalog'

import CatalogTemplatePreviewModal from '../CatalogTemplatePreviewModal'

import CatalogMediaCard from '~/components/CatalogMediaCard'
import AddToModal from '~/components/CatalogModal'
import { FiltersContent } from '~/components/Filters'
import SelectInput from '~/components/Inputs/Basic/SelectInput'
import TextInput from '~/components/Inputs/Basic/TextInput'
import Layout from '~/components/Layouts/Layout'
import MwMLoader from '~/components/MwMLoader'
import ViewTitle from '~/components/ViewTitle'
import { FASTLY_PATH } from '~/const/asset'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import {
  useGetTemplateById,
  useMostLikedTemplate,
  usePopularTemplate,
  useRecentTemplate,
  useSearchTemplates,
} from '~/queries/catalog'
import { debounce } from '~/utils/function'

import './styles.scss'

const selectionOptions: IOption[] = [
  {
    label: 'Feed Popular',
    value: 'feed_popular',
  },
  {
    label: 'Feed Most Liked',
    value: 'feed_most_liked',
  },
  {
    label: 'Feed Recent',
    value: 'feed_recent',
  },
  {
    label: 'Search',
    value: 'search',
  },
]

const templateOriginOptions: IOption[] = [
  {
    label: 'All',
    value: '',
  },
  {
    label: 'ML Generation',
    value: 'ml',
  },
  {
    label: 'Catalog',
    value: 'catalog',
  },
]

const Explore = (): ReactElement => {
  /** Local states  */

  const catalogRef = useRef<HTMLInputElement>(null)

  const searchByNameRef = useRef<HTMLInputElement>(null)

  const searchByIdRef = useRef<HTMLInputElement>(null)

  const { inView, ref } = useInView()

  const [search, setSearch] = useState('')

  const [templateId, setTemplateId] = useState('')

  const [focusedInput, setFocusedInput] = useState<'search' | 'postId'>('search')

  const [selectedFeedType, setSelectedFeedType] = useState('feed_popular')

  const [templates, setTemplates] = useState<ICatalogTemplate[] | undefined>()

  const [selectedTemplate, setSelectedTemplate] = useState('')

  const [selectedTemplateOrigin, setSelectedTemplateOrigin] = useState<string>()

  const [selectedTemplatePreviewUrl, setSelectedTemplatePreviewUrl] = useState('')

  const [isAddToModalOpen, setIsAddToModalOpen] = useState(false)

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)

  const [selectedModal, setSelectedModal] = useState<'category' | 'daily'>('category')

  /** Queries & Mutations */

  const {
    data: searchTemplatesData,
    fetchNextPage: fetchSearchTemplatesNextPage,
    isError: isSearchTemplatesError,
    isFetchingNextPage: isSearchTemplatesFetchingNextPage,
    isLoading: isSearchTemplatesLoading,
  } = useSearchTemplates({
    query: search,
  })

  const {
    data: recentTemplatesData,
    fetchNextPage: fetchRecentTemplatesNextPage,
    isError: isRecentTemplatesError,
    isFetchingNextPage: isRecentTemplateFetchingNextPage,
    isLoading: isRecentTemplateLoading,
  } = useRecentTemplate({
    originType: selectedTemplateOrigin,
  })

  const { data: popularTemplates, isLoading: isPopularTemplatesLoading } = usePopularTemplate({
    isEnabled: selectedFeedType === 'feed_popular',
    originType: selectedTemplateOrigin,
  })

  const { data: mostLikedTemplates, isLoading: isMostLikedTemplatesLoading } = useMostLikedTemplate({
    isEnabled: selectedFeedType === 'feed_most_liked',
    originType: selectedTemplateOrigin,
  })

  const {
    data: templateByIdData,
    isError: isTemplateByIdError,
    isLoading: isTemplateByIdLoading,
  } = useGetTemplateById({
    isEnabled: selectedFeedType === 'search' && focusedInput === 'postId' && templateId !== '',
    templateId: templateId,
  })

  const isApiLoading =
    isSearchTemplatesLoading ||
    isPopularTemplatesLoading ||
    isMostLikedTemplatesLoading ||
    isRecentTemplateLoading ||
    isTemplateByIdLoading

  /** Handlers */

  const handleCatalogMediaCardClick = (mediaUrl: string) => {
    setSelectedTemplatePreviewUrl(mediaUrl)
    setIsPreviewModalOpen(true)
  }

  const handleSearchChange = debounce((event: ChangeEvent<HTMLInputElement>) => {
    const search = event.target.value
    setSearch(search)
  }, 500)

  const handleIdChange = debounce((event: ChangeEvent<HTMLInputElement>) => {
    const id = event.target.value
    setTemplateId(id)
  }, 500)

  const handleSelectInputChange = (value: IOption | null, name?: 'template_origin' | 'selection') => {
    switch (name) {
      case 'template_origin':
        setSelectedTemplateOrigin(value?.value)
        break
      case 'selection':
        setSelectedFeedType(value?.value || 'feed_popular')
        break
    }
  }

  const handleOpenAddToModal = (templateId: string, type: 'daily' | 'category') => {
    setIsAddToModalOpen(true)
    setSelectedModal(type)
    setSelectedTemplate(templateId)
  }

  const handleCloseAddToModal = () => setIsAddToModalOpen(false)

  const handleFocus = (inputName: string) => {
    if (inputName === 'search') {
      setFocusedInput('search')
      setTemplateId('')
      if (searchByIdRef.current) searchByIdRef.current.value = ''
    } else {
      setFocusedInput('postId')
      setSearch('')
      if (searchByNameRef.current) searchByNameRef.current.value = ''
    }
  }

  const hasNextPage = (): boolean => {
    if (selectedFeedType === 'search' && searchTemplatesData) {
      return searchTemplatesData.pages[0].next_page_cursor !== ''
    } else if (selectedFeedType === 'feed_recent' && recentTemplatesData) {
      return recentTemplatesData.pages[0].next_page_cursor !== ''
    }

    return false
  }

  const handlePreviewModalState = (isOpen: boolean) => () => setIsPreviewModalOpen(isOpen)

  const getDataSource = useCallback(() => {
    switch (selectedFeedType) {
      case 'feed_recent':
        return setTemplates(recentTemplatesData?.pages.flatMap((page) => page.data || []) as ICatalogTemplate[])
      case 'feed_popular':
        return setTemplates(popularTemplates)
      case 'feed_most_liked':
        return setTemplates(mostLikedTemplates)
      default: {
        return setTemplates(
          focusedInput === 'search'
            ? (searchTemplatesData?.pages.flatMap((page) => page.data || []) as ICatalogTemplate[])
            : templateByIdData && ([templateByIdData] as ICatalogTemplate[])
        )
      }
    }
  }, [
    focusedInput,
    mostLikedTemplates,
    popularTemplates,
    recentTemplatesData?.pages,
    templateByIdData,
    searchTemplatesData?.pages,
    selectedFeedType,
  ])

  /** Effects */

  useDocumentTitle('Catalog - Explore')

  useEffect(() => {
    getDataSource()
  }, [getDataSource])

  useEffect(() => {
    if (inView) {
      if (selectedFeedType === 'feed_recent') {
        !isRecentTemplateLoading && !isRecentTemplateFetchingNextPage && fetchRecentTemplatesNextPage()
      } else if (selectedFeedType === 'search') {
        !isSearchTemplatesLoading && !isSearchTemplatesFetchingNextPage && fetchSearchTemplatesNextPage()
      }
    } else return
  }, [
    fetchRecentTemplatesNextPage,
    fetchSearchTemplatesNextPage,
    inView,
    isRecentTemplateFetchingNextPage,
    isRecentTemplateLoading,
    isSearchTemplatesLoading,
    isSearchTemplatesFetchingNextPage,
    selectedFeedType,
  ])

  /** Render */

  return (
    <div className="catalog-explore-view">
      <ViewTitle>Explore</ViewTitle>
      <FiltersContent className="catalog-explore-view__filters">
        <div className="catalog-explore-view__filters__left">
          <TextInput
            disabled={selectedFeedType !== 'search'}
            icon={<MdOutlineSearch size={16} />}
            label="Search"
            onChange={handleSearchChange}
            onFocus={() => handleFocus('search')}
            placeholder="Enter keyword"
            ref={searchByNameRef}
          />
          <TextInput
            disabled={selectedFeedType !== 'search'}
            icon={<MdOutlineSearch size={16} />}
            label="Get by ID"
            onChange={handleIdChange}
            onFocus={() => handleFocus('postId')}
            placeholder="Enter template ID"
            ref={searchByIdRef}
          />
        </div>
        <div className="catalog-explore-view__filters__right">
          <SelectInput
            label="Selection"
            onChange={(value) => handleSelectInputChange(value as IOption | null, 'selection')}
            options={selectionOptions}
            value={selectionOptions.find(({ value }) => value === selectedFeedType)}
          />
          <SelectInput
            disabled={selectedFeedType === 'search'}
            label="Template origin"
            onChange={(value) => handleSelectInputChange(value as IOption | null, 'template_origin')}
            options={templateOriginOptions}
            value={templateOriginOptions.find(({ value }) => value === selectedTemplateOrigin)}
          />
        </div>
      </FiltersContent>
      <Layout withMargin>
        {isApiLoading ? (
          <MwMLoader category="general" />
        ) : (
          <div className="catalog-explore-view__content__grid" ref={catalogRef}>
            {focusedInput === 'postId' && isTemplateByIdError ? (
              <span className="catalog-explore-view__content__grid__caption">
                A template with the ID &quot;{templateId}&quot; could not be found.
              </span>
            ) : (
              templates?.map(
                (
                  {
                    categories,
                    cleaned_prompt,
                    coloring_template_id,
                    origin_type,
                    pdf_file_id,
                    preview_png_file_id,
                  }: ICatalogTemplate,
                  idx
                ) => {
                  return (
                    <CatalogMediaCard
                      categories={categories}
                      coloringTemplateId={coloring_template_id}
                      key={`${pdf_file_id}/${idx}`}
                      mediaUrl={`${FASTLY_PATH}${preview_png_file_id}` || ''}
                      onOpenAddToCategoryModal={() => handleOpenAddToModal(coloring_template_id, 'category')}
                      onOpenAddToDailyModal={() => handleOpenAddToModal(coloring_template_id, 'daily')}
                      prompt={cleaned_prompt}
                      templateOrigin={origin_type}
                      zoomOnClick={handleCatalogMediaCardClick}
                    />
                  )
                }
              )
            )}
            {hasNextPage() && !isRecentTemplatesError && !isSearchTemplatesError ? (
              <div ref={ref}>
                {isSearchTemplatesFetchingNextPage || isRecentTemplateFetchingNextPage ? (
                  <MwMLoader category="item" />
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </Layout>
      <CatalogTemplatePreviewModal
        isOpen={isPreviewModalOpen}
        mediaUrl={selectedTemplatePreviewUrl}
        onClose={handlePreviewModalState(false)}
      />
      <AddToModal
        isOpen={isAddToModalOpen}
        onClose={handleCloseAddToModal}
        selectedTemplate={selectedTemplate}
        type={selectedModal}
      />
    </div>
  )
}
export default Explore
